import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { reviews, faqs } from '../data/content';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlus, FiMinus, FiStar, FiMapPin } = FiIcons;

export default function InfoSection() {
  const [activeTab, setActiveTab] = useState('faq');
  const [openFaq, setOpenFaq] = useState(0);

  const tabs = [
    { id: 'faq', label: 'THE WORKSPACE' },
    { id: 'shipping', label: 'LOGISTICS' },
    { id: 'care', label: 'STEWARDSHIP' }
  ];

  return (
    <section id="guides" className="py-24 md:py-32 bg-[var(--off-white)]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-20">
        <div className="lg:col-span-5">
          <div className="mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[var(--navy)] mb-4 italic">Nest Notes</h2>
            <p className="text-gray-500">Real feedback from our circular community.</p>
          </div>
          
          <div className="space-y-8">
            {reviews.map((review) => (
              <motion.div 
                key={review.id}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="group"
              >
                <div className="flex text-[var(--mint-dark)] mb-4 gap-1">
                  {[...Array(5)].map((_, i) => <SafeIcon key={i} icon={FiStar} className="fill-current text-xs" />)}
                </div>
                <p className="text-lg text-[var(--navy-light)] italic mb-6 leading-relaxed">"{review.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[var(--kraft)] flex items-center justify-center font-bold text-[var(--navy)] text-xs">
                    {review.author[0]}
                  </div>
                  <div>
                    <div className="font-bold text-[var(--navy)] text-sm">{review.author}</div>
                    <div className="flex items-center gap-1 text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                      <SafeIcon icon={FiMapPin} /> {review.location}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="flex space-x-12 border-b border-gray-200 mb-12 overflow-x-auto hide-scrollbar">
            {tabs.map(tab => (
              <button 
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-4 text-[10px] font-bold tracking-[0.3em] uppercase whitespace-nowrap transition-all relative ${activeTab === tab.id ? 'text-[var(--navy)]' : 'text-gray-400 hover:text-gray-600'}`}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <motion.div layoutId="activeTabIndicator" className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--navy)]" />
                )}
              </button>
            ))}
          </div>

          <div className="min-h-[500px]">
            <AnimatePresence mode="wait">
              {activeTab === 'faq' && (
                <motion.div key="faq" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                  <div className="space-y-4">
                    {faqs.map((faq, idx) => (
                      <div key={idx} className="bg-white border-b border-gray-100 last:border-0 overflow-hidden">
                        <button 
                          onClick={() => setOpenFaq(openFaq === idx ? -1 : idx)}
                          className="w-full flex justify-between items-center py-6 text-left font-bold text-[var(--navy)] hover:text-[var(--mint-dark)] transition-colors group"
                        >
                          <span className="text-lg md:text-xl pr-8">{faq.q}</span>
                          <div className={`p-2 rounded-full border border-gray-100 group-hover:border-[var(--mint)] transition-all ${openFaq === idx ? 'bg-[var(--navy)] text-white' : ''}`}>
                            <SafeIcon icon={openFaq === idx ? FiMinus : FiPlus} className="text-sm" />
                          </div>
                        </button>
                        <AnimatePresence>
                          {openFaq === idx && (
                            <motion.div 
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="overflow-hidden"
                            >
                              <div className="pb-8 text-gray-500 leading-relaxed max-w-2xl">
                                {faq.a}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {activeTab === 'shipping' && (
                <motion.div key="shipping" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-8">
                  <div className="bg-white p-10 rounded-2xl border border-gray-100 shadow-sm">
                    <h3 className="text-2xl font-bold text-[var(--navy)] mb-4 italic">Carbon-Neutral Logistics</h3>
                    <p className="text-gray-500 leading-relaxed">
                      We've optimized our logistics to ensure the shortest path from our fulfillment center to your door.
                    </p>
                  </div>
                  {/* Context Image: Organized Under-sink Storage */}
                  <div className="relative aspect-video rounded-2xl overflow-hidden shadow-xl">
                    <img 
                      src="https://images.unsplash.com/photo-1595433707802-68267d83760a?auto=format&fit=crop&q=80&w=1000" 
                      alt="Organized home storage cupboard with glass refill bottles and cleaning supplies" 
                      className="w-full h-full object-cover"
                      width="800"
                      height="450"
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest text-[var(--navy)]">
                      The Organized Workspace
                    </div>
                  </div>
                </motion.div>
              )}

              {activeTab === 'care' && (
                <motion.div key="care" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-8">
                  <div className="bg-white p-10 rounded-2xl border border-gray-100 shadow-sm">
                    <h3 className="text-2xl font-bold text-[var(--navy)] mb-4 italic">Stewardship Guide</h3>
                    <p className="text-gray-500 leading-relaxed">
                      Your glass vessels are designed for a lifetime of use. Treat them with care, and they will serve your home for years to come.
                    </p>
                  </div>
                  <div className="space-y-4">
                    <div className="flex gap-4 p-4 bg-[var(--kraft-light)] rounded-lg">
                      <div className="w-8 h-8 rounded-full bg-[var(--navy)] text-[var(--mint)] flex items-center justify-center font-bold text-xs">1</div>
                      <p className="text-sm text-[var(--navy)] font-medium">Rinse bottles between refills to prevent mineral buildup from tap water.</p>
                    </div>
                    <div className="flex gap-4 p-4 bg-[var(--kraft-light)] rounded-lg">
                      <div className="w-8 h-8 rounded-full bg-[var(--navy)] text-[var(--mint)] flex items-center justify-center font-bold text-xs">2</div>
                      <p className="text-sm text-[var(--navy)] font-medium">Silicone sleeves are removable and dishwasher safe for deep cleaning.</p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}